import { pgTable, uuid, varchar, text, integer, timestamp, jsonb, index, boolean, decimal } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";

// Tool types enum for type safety
export const TOOL_TYPES = [
  "try-on",
  "staging",
  "ads",
  "designer",
  "visualizer",
  "unboxing",
] as const;

export type ToolType = (typeof TOOL_TYPES)[number];

// Subscription tiers
export const SUBSCRIPTION_TIERS = ["free", "starter", "pro", "business", "enterprise"] as const;
export type SubscriptionTier = (typeof SUBSCRIPTION_TIERS)[number];

// Project statuses
export const PROJECT_STATUSES = ["draft", "processing", "completed", "failed"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

// Asset types
export const ASSET_TYPES = ["image", "video", "text"] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

// ============================================
// TABLES
// ============================================

/**
 * Users table - synced with Clerk
 */
export const users = pgTable("users", {
  id: uuid("id").primaryKey().defaultRandom(),
  clerkId: varchar("clerk_id", { length: 255 }).unique().notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  name: varchar("name", { length: 255 }),
  subscriptionTier: varchar("subscription_tier", { length: 50 }).default("free").notNull(),
  creditsRemaining: integer("credits_remaining").default(50).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

/**
 * Projects table - generic for all tools
 */
export const projects = pgTable(
  "projects",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    toolType: varchar("tool_type", { length: 50 }).notNull(),
    name: varchar("name", { length: 255 }).notNull(),
    status: varchar("status", { length: 50 }).default("draft").notNull(),
    inputData: jsonb("input_data"),
    outputData: jsonb("output_data"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_projects_user_id").on(table.userId),
    index("idx_projects_tool_type").on(table.toolType),
  ]
);

/**
 * Generated Assets table - files created by tools
 */
export const generatedAssets = pgTable(
  "generated_assets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    projectId: uuid("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    assetType: varchar("asset_type", { length: 50 }).notNull(),
    url: text("url"),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [index("idx_assets_project_id").on(table.projectId)]
);

/**
 * Usage Logs table - credit tracking
 */
export const usageLogs = pgTable(
  "usage_logs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    toolType: varchar("tool_type", { length: 50 }).notNull(),
    creditsUsed: integer("credits_used").default(1).notNull(),
    timestamp: timestamp("timestamp").defaultNow().notNull(),
  },
  (table) => [index("idx_usage_user_id").on(table.userId)]
);

/**
 * Affiliate Applications table
 */
export const affiliateApplications = pgTable(
  "affiliate_applications",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    fullName: varchar("full_name", { length: 255 }).notNull(),
    email: varchar("email", { length: 255 }).unique().notNull(),
    website: text("website"),
    platform: varchar("platform", { length: 100 }), // youtube, tiktok, blog, agency, etc.
    audienceSize: varchar("audience_size", { length: 50 }), // <1k, 1k-10k, 10k-100k, 100k+
    paypalEmail: varchar("paypal_email", { length: 255 }),
    message: text("message"),
    status: varchar("status", { length: 50 }).default("pending").notNull(), // pending, approved, rejected
    rewardfulAffiliateId: varchar("rewardful_affiliate_id", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_affiliate_apps_email").on(table.email),
    index("idx_affiliate_apps_status").on(table.status),
  ]
);

/**
 * Affiliate Referrals table - tracks referred subscriptions
 */
export const affiliateReferrals = pgTable(
  "affiliate_referrals",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    affiliateApplicationId: uuid("affiliate_application_id")
      .references(() => affiliateApplications.id, { onDelete: "cascade" })
      .notNull(),
    referredUserId: uuid("referred_user_id").references(() => users.id, { onDelete: "set null" }),
    referredEmail: varchar("referred_email", { length: 255 }),
    subscriptionTier: varchar("subscription_tier", { length: 50 }),
    billingPeriod: varchar("billing_period", { length: 20 }), // monthly, annual
    revenueAmount: decimal("revenue_amount", { precision: 10, scale: 2 }),
    commissionAmount: decimal("commission_amount", { precision: 10, scale: 2 }),
    commissionRate: decimal("commission_rate", { precision: 5, scale: 2 }), // e.g. 25.00 for 25%
    status: varchar("status", { length: 50 }).default("pending").notNull(), // pending, paid, clawed_back
    isAnnual: boolean("is_annual").default(false).notNull(),
    clawbackDeadline: timestamp("clawback_deadline"), // 90 days from subscription start
    paidAt: timestamp("paid_at"),
    rewardfulReferralId: varchar("rewardful_referral_id", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_referrals_affiliate_id").on(table.affiliateApplicationId),
    index("idx_referrals_status").on(table.status),
  ]
);

// ============================================
// RELATIONS
// ============================================

export const usersRelations = relations(users, ({ many }) => ({
  projects: many(projects),
  usageLogs: many(usageLogs),
}));

export const projectsRelations = relations(projects, ({ one, many }) => ({
  user: one(users, {
    fields: [projects.userId],
    references: [users.id],
  }),
  assets: many(generatedAssets),
}));

export const generatedAssetsRelations = relations(generatedAssets, ({ one }) => ({
  project: one(projects, {
    fields: [generatedAssets.projectId],
    references: [projects.id],
  }),
}));

export const usageLogsRelations = relations(usageLogs, ({ one }) => ({
  user: one(users, {
    fields: [usageLogs.userId],
    references: [users.id],
  }),
}));

export const affiliateApplicationsRelations = relations(affiliateApplications, ({ many }) => ({
  referrals: many(affiliateReferrals),
}));

export const affiliateReferralsRelations = relations(affiliateReferrals, ({ one }) => ({
  application: one(affiliateApplications, {
    fields: [affiliateReferrals.affiliateApplicationId],
    references: [affiliateApplications.id],
  }),
  referredUser: one(users, {
    fields: [affiliateReferrals.referredUserId],
    references: [users.id],
  }),
}));

// ============================================
// TYPE EXPORTS (for use in components)
// ============================================

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

export type Project = typeof projects.$inferSelect;
export type NewProject = typeof projects.$inferInsert;

export type GeneratedAsset = typeof generatedAssets.$inferSelect;
export type NewGeneratedAsset = typeof generatedAssets.$inferInsert;

export type UsageLog = typeof usageLogs.$inferSelect;
export type NewUsageLog = typeof usageLogs.$inferInsert;

export type AffiliateApplication = typeof affiliateApplications.$inferSelect;
export type NewAffiliateApplication = typeof affiliateApplications.$inferInsert;

export type AffiliateReferral = typeof affiliateReferrals.$inferSelect;
export type NewAffiliateReferral = typeof affiliateReferrals.$inferInsert;
